// src/pages/subscription/SubscriptionCreatePage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';

/**
 * Subscription create page.
 */
export const SubscriptionCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useSubscription();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    plan: '',
    tenant: '',
    billing_account: '',
    latest_invoice: '',
    owner: '',
    status: '',
    quantity: 0,
    current_price: 0,
    currency: '',
    billing_cycle: '',
    payment_method_id: '',
    latest_invoice_id: '',
    current_period_start: '',
    current_period_end: '',
    trial_start: '',
    trial_end: '',
    started_at: '',
    ends_at: '',
    cancelled_at: '',
    cancellation_reason: '',
    cancellation_comment: '',
    renewal_settings: '',
    features: '',
    usage: '',
    applied_discounts: '',
    items: '',
    metadata: '',
  });

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await store.CreateSubscription(formData as any);
      navigate('/subscriptions');
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">{t('subscription.create')}</h1>
        <nav className="text-sm">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
            {t('subscription.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.create')}</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('subscription.details')}</CardTitle>
            <CardDescription>{t('subscription.fillInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Plan"
              type="text"
              value={formData.plan}
              onChange={(e) => handleChange('plan', e.target.value)}
              placeholder="Plan"
              required
            />
            <Input
              label="Tenant"
              type="text"
              value={formData.tenant}
              onChange={(e) => handleChange('tenant', e.target.value)}
              placeholder="Tenant"
              required
            />
            <Input
              label="Billing Account"
              type="text"
              value={formData.billing_account}
              onChange={(e) => handleChange('billing_account', e.target.value)}
              placeholder="Billing Account"
              required
            />
            <Input
              label="Latest Invoice"
              type="text"
              value={formData.latest_invoice}
              onChange={(e) => handleChange('latest_invoice', e.target.value)}
              placeholder="Latest Invoice"
              required
            />
            <Input
              label="Owner"
              type="text"
              value={formData.owner}
              onChange={(e) => handleChange('owner', e.target.value)}
              placeholder="Owner"
              required
            />
            <Input
              label="Status"
              type="text"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              placeholder="Status"
              required
            />
            <Input
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', Number(e.target.value))}
              placeholder="Quantity"
              required
            />
            <Input
              label="Current Price"
              type="number"
              value={formData.current_price}
              onChange={(e) => handleChange('current_price', Number(e.target.value))}
              placeholder="Current Price"
              required
            />
            <Input
              label="Currency"
              type="text"
              value={formData.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              placeholder="Currency"
              required
            />
            <Input
              label="Billing Cycle"
              type="text"
              value={formData.billing_cycle}
              onChange={(e) => handleChange('billing_cycle', e.target.value)}
              placeholder="Billing Cycle"
              required
            />
            <Input
              label="Payment Method Id"
              type="text"
              value={formData.payment_method_id}
              onChange={(e) => handleChange('payment_method_id', e.target.value)}
              placeholder="Payment Method Id"
              required
            />
            <Input
              label="Latest Invoice Id"
              type="text"
              value={formData.latest_invoice_id}
              onChange={(e) => handleChange('latest_invoice_id', e.target.value)}
              placeholder="Latest Invoice Id"
              required
            />
            <Input
              label="Current Period Start"
              type="text"
              value={formData.current_period_start}
              onChange={(e) => handleChange('current_period_start', e.target.value)}
              placeholder="Current Period Start"
              required
            />
            <Input
              label="Current Period End"
              type="text"
              value={formData.current_period_end}
              onChange={(e) => handleChange('current_period_end', e.target.value)}
              placeholder="Current Period End"
              required
            />
            <Input
              label="Trial Start"
              type="text"
              value={formData.trial_start}
              onChange={(e) => handleChange('trial_start', e.target.value)}
              placeholder="Trial Start"
            />
            <Input
              label="Trial End"
              type="text"
              value={formData.trial_end}
              onChange={(e) => handleChange('trial_end', e.target.value)}
              placeholder="Trial End"
            />
            <Input
              label="Started At"
              type="text"
              value={formData.started_at}
              onChange={(e) => handleChange('started_at', e.target.value)}
              placeholder="Started At"
              required
            />
            <Input
              label="Ends At"
              type="text"
              value={formData.ends_at}
              onChange={(e) => handleChange('ends_at', e.target.value)}
              placeholder="Ends At"
            />
            <Input
              label="Cancelled At"
              type="text"
              value={formData.cancelled_at}
              onChange={(e) => handleChange('cancelled_at', e.target.value)}
              placeholder="Cancelled At"
            />
            <Input
              label="Cancellation Reason"
              type="text"
              value={formData.cancellation_reason}
              onChange={(e) => handleChange('cancellation_reason', e.target.value)}
              placeholder="Cancellation Reason"
              required
            />
            <Input
              label="Cancellation Comment"
              type="text"
              value={formData.cancellation_comment}
              onChange={(e) => handleChange('cancellation_comment', e.target.value)}
              placeholder="Cancellation Comment"
              required
            />
            <Input
              label="Renewal Settings"
              type="text"
              value={formData.renewal_settings}
              onChange={(e) => handleChange('renewal_settings', e.target.value)}
              placeholder="Renewal Settings"
              required
            />
            <Input
              label="Features"
              type="text"
              value={formData.features}
              onChange={(e) => handleChange('features', e.target.value)}
              placeholder="Features"
              required
            />
            <Input
              label="Usage"
              type="text"
              value={formData.usage}
              onChange={(e) => handleChange('usage', e.target.value)}
              placeholder="Usage"
              required
            />
            <Input
              label="Applied Discounts"
              type="text"
              value={formData.applied_discounts}
              onChange={(e) => handleChange('applied_discounts', e.target.value)}
              placeholder="Applied Discounts"
              required
            />
            <Input
              label="Items"
              type="text"
              value={formData.items}
              onChange={(e) => handleChange('items', e.target.value)}
              placeholder="Items"
              required
            />
            <Input
              label="Metadata"
              type="text"
              value={formData.metadata}
              onChange={(e) => handleChange('metadata', e.target.value)}
              placeholder="Metadata"
              required
            />
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to="/subscriptions">
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.creating') : t('subscription.create')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default SubscriptionCreatePage;
